import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import {
  Box,
  Button,
  ButtonBase,
  Collapse,
  MenuItem,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import {
  SETTING_CODE_ORDER,
  SETTING_GROUP_ORDER,
  US_STATES,
  settingValueSchema,
} from '@app/shared';
import type { SettingChangeDto, SettingDto, SettingType, SettingValue } from '@app/shared';
import { DataTable, rowNumber } from '../../components/DataTable';
import type { Column } from '../../components/DataTable';
import { useNotify } from '../../components/NotifyProvider';
import { PageHeader } from '../../components/PageHeader';
import { ErrorState, LoadingState } from '../../components/PageState';
import {
  MoneyInput,
  MultiSelectInput,
  NumberInput,
  SelectInput,
  TextInput,
  applyServerErrors,
} from '../../components/form';
import { formatCents, formatDateTime } from '../../lib/format';
import { useFeatureReleased } from '../../release';
import { useSaveSettings, useSettingChanges, useSettings } from './api';

interface FormValues {
  // null is an emptied number field: React Hook Form shows the default again for `undefined`.
  values: Record<string, SettingValue | null | undefined>;
}

const STATE_OPTIONS = US_STATES.map(([code, name]) => ({
  value: code,
  label: `${code} - ${name}`,
}));

const INVALID: Record<SettingType, string> = {
  boolean: 'Select Yes or No',
  number: 'Enter a whole number',
  money: 'Enter a valid amount',
  text: 'At most 200 characters',
  states: 'Select states from the list',
  select: 'Select one of the choices',
};

const settingLabel = (s: { label: string; code: string | null }) =>
  s.code ? `${s.label} (${s.code})` : s.label;

/** How a value reads in the change log. */
function showValue(type: SettingType | undefined, value: SettingValue): string {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'None';
  if (type === 'money' && typeof value === 'number') return formatCents(value);
  return value === '' ? '(empty)' : String(value);
}

/** Release switch of every other reference setting, the reference's order and its collapsed groups. */
const ALL_SETTINGS = 'system-config-all';
const NOT_USED =
  'Not used by 212Ez yet: kept so System Config holds every setting of the reference.';

/** Yes / No radios, the control most reference settings use, in the reference's own two words. */
function YesNoRadio(props: {
  control: Control<FormValues>;
  name: `values.${string}`;
  label: string;
  helperText: string;
  words?: readonly string[];
}) {
  const [yes, no] = props.words?.length === 2 ? props.words : ['Yes', 'No'];
  const id = `setting-${props.name}`;
  return (
    <Controller
      control={props.control}
      name={props.name}
      render={({ field, fieldState }) => (
        <FormControl error={Boolean(fieldState.error)}>
          <FormLabel id={id} sx={{ fontSize: 13 }}>
            {props.label}
          </FormLabel>
          <RadioGroup
            row
            aria-labelledby={id}
            name={field.name}
            value={field.value ? 'yes' : 'no'}
            onChange={(e) => field.onChange(e.target.value === 'yes')}
          >
            <FormControlLabel value="yes" control={<Radio size="small" />} label={yes} />
            <FormControlLabel value="no" control={<Radio size="small" />} label={no} />
          </RadioGroup>
          <FormHelperText sx={{ mx: 0, mt: -0.5 }}>
            {fieldState.error?.message ?? props.helperText}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
}

function SettingField({ setting, control }: { setting: SettingDto; control: Control<FormValues> }) {
  const name = `values.${setting.key}` as const;
  const label = settingLabel(setting);
  // A setting nothing reads yet says so instead of a help text.
  setting = setting.inert ? { ...setting, help: setting.help || NOT_USED } : setting;
  switch (setting.type) {
    case 'boolean':
      return (
        <YesNoRadio
          control={control}
          name={name}
          label={label}
          helperText={setting.help}
          words={setting.options}
        />
      );
    case 'number':
      return (
        <NumberInput
          control={control}
          name={name}
          label={label}
          helperText={setting.help}
          emptyValue={null}
        />
      );
    case 'money':
      return <MoneyInput control={control} name={name} label={label} helperText={setting.help} />;
    case 'select':
      return (
        <SelectInput
          control={control}
          name={name}
          label={label}
          helperText={setting.help}
          options={(setting.options ?? []).map((o) => ({ value: o, label: o }))}
        />
      );
    case 'states':
      return (
        <MultiSelectInput
          control={control}
          name={name}
          label={label}
          helperText={setting.help}
          options={STATE_OPTIONS}
        />
      );
    default:
      return (
        <TextInput
          control={control}
          name={name}
          label={label}
          helperText={setting.help}
          slotProps={{ htmlInput: { maxLength: 200 } }}
        />
      );
  }
}

/** "Config Changes logs": who changed which setting, from what to what. */
function ChangesDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const changes = useSettingChanges({ page, pageSize }, open);
  const settings = useSettings();
  const typeOf = (key: string) => settings.data?.find((s) => s.key === key)?.type;

  const columns: Column<SettingChangeDto>[] = [
    { key: '#', header: '#', width: 50, render: (_c, i) => rowNumber(i, page, pageSize) },
    { key: 'label', header: 'Config', render: (c) => settingLabel(c) },
    { key: 'from', header: 'Old Value', render: (c) => showValue(typeOf(c.key), c.from) },
    { key: 'to', header: 'New Value', render: (c) => showValue(typeOf(c.key), c.to) },
    { key: 'by', header: 'Changed By', render: (c) => c.by?.loginId ?? '' },
    { key: 'at', header: 'Date Time', nowrap: true, render: (c) => formatDateTime(c.at) },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Config Changes logs</DialogTitle>
      <DialogContent dividers>
        <DataTable
          label="Config Changes logs"
          columns={columns}
          rows={changes.data?.data}
          rowKey={(c) => c.id}
          loading={changes.isFetching}
          error={changes.error}
          onRetry={() => void changes.refetch()}
          emptyText="No setting has been changed yet."
          page={page}
          pageSize={pageSize}
          total={changes.data?.total ?? 0}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          pageSizeOptions={[10, 20, 50]}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

const toFormValues = (settings: SettingDto[]): FormValues => ({
  values: Object.fromEntries(settings.map((s) => [s.key, s.value])),
});

function SettingsForm({ settings }: { settings: SettingDto[] }) {
  const notify = useNotify();
  const save = useSaveSettings();
  const { control, handleSubmit, reset, setError } = useForm<FormValues>({
    defaultValues: toFormValues(settings),
  });

  // Show what the server holds after a save (or after another user's change).
  useEffect(() => reset(toFormValues(settings)), [settings, reset]);

  const featureOn = useFeatureReleased();
  const reference = featureOn(ALL_SETTINGS);
  const categories = useMemo(() => {
    const byCategory = new Map<string, SettingDto[]>();
    // A setting added after a deployment is shown once its release switch is on.
    for (const s of settings.filter((x) => !x.feature || featureOn(x.feature)))
      byCategory.set(s.category, [...(byCategory.get(s.category) ?? []), s]);
    const entries = [...byCategory.entries()];
    if (!reference) return entries;
    // The reference's order: its groups, then each setting where it stands on its page.
    const groupAt = (g: string) => {
      const i = SETTING_GROUP_ORDER.indexOf(g);
      return i < 0 ? SETTING_GROUP_ORDER.length : i;
    };
    const codeAt = (s: SettingDto) => (s.code ? (SETTING_CODE_ORDER[s.code] ?? 9_999) : 10_000);
    return entries
      .map(([g, items]) => [g, [...items].sort((a, b) => codeAt(a) - codeAt(b))] as const)
      .sort(([a], [b]) => groupAt(a) - groupAt(b));
  }, [settings, featureOn, reference]);
  // The reference's groups start collapsed; "Search System Config" opens one.
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (group: string) =>
    setOpen((now) => {
      const next = new Set(now);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  const jumpTo = (group: string) => {
    setOpen((now) => new Set(now).add(group));
    requestAnimationFrame(() =>
      document
        .getElementById(`config-group-${group.replace(/[^A-Za-z0-9]/g, '-')}`)
        ?.scrollIntoView?.({ behavior: 'smooth', block: 'start' }),
    );
  };

  const submit = handleSubmit(async ({ values }) => {
    const changed: Record<string, SettingValue> = {};
    let invalid = false;
    for (const s of settings) {
      const value = values[s.key];
      if (JSON.stringify(value) === JSON.stringify(s.value)) continue;
      const parsed = settingValueSchema(s).safeParse(value);
      if (parsed.success) changed[s.key] = parsed.data as SettingValue;
      else {
        setError(`values.${s.key}`, { type: 'validate', message: INVALID[s.type] });
        invalid = true;
      }
    }
    if (invalid) return;
    const count = Object.keys(changed).length;
    if (count === 0) {
      notify.info('Nothing was changed.');
      return;
    }
    try {
      await save.mutateAsync(changed);
      notify.success(count === 1 ? 'Setting saved.' : `${count} settings saved.`);
    } catch (err) {
      if (!applyServerErrors(err, setError)) notify.error(err);
    }
  });

  const grid = (items: readonly SettingDto[]) => (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
        alignItems: 'start',
      }}
    >
      {items.map((s) => (
        <SettingField key={s.key} setting={s} control={control} />
      ))}
    </Box>
  );

  return (
    <Box component="form" noValidate onSubmit={(e) => void submit(e)} aria-label="System Config">
      {reference ? (
        <TextField
          select
          label="Search System Config"
          value=""
          onChange={(e) => jumpTo(e.target.value)}
          sx={{ minWidth: 320, mb: 1.5 }}
        >
          <MenuItem value="" sx={{ display: 'none' }} />
          {categories.map(([category]) => (
            <MenuItem key={category} value={category}>
              {category}
            </MenuItem>
          ))}
        </TextField>
      ) : null}
      {categories.map(([category, items]) =>
        reference ? (
          <Paper
            key={category}
            id={`config-group-${category.replace(/[^A-Za-z0-9]/g, '-')}`}
            variant="outlined"
            component="section"
            aria-label={category}
            sx={{ mb: 1, scrollMarginTop: 72 }}
          >
            <ButtonBase
              onClick={() => toggle(category)}
              aria-expanded={open.has(category)}
              sx={{ width: '100%', justifyContent: 'space-between', px: 2, py: 1.25 }}
            >
              <Typography variant="h3" component="span">
                {category}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {items.length} {items.length === 1 ? 'setting' : 'settings'}
                </Typography>
                <ExpandMoreIcon
                  fontSize="small"
                  sx={{ transform: open.has(category) ? 'rotate(180deg)' : 'none' }}
                />
              </Box>
            </ButtonBase>
            <Collapse in={open.has(category)}>
              <Box sx={{ px: 2, pb: 2 }}>{grid(items)}</Box>
            </Collapse>
          </Paper>
        ) : (
          <Paper
            key={category}
            variant="outlined"
            component="section"
            aria-label={category}
            sx={{ p: 2, mb: 1.5 }}
          >
            <Typography variant="h3" sx={{ mb: 1.5 }}>
              {category}
            </Typography>
            {grid(items)}
          </Paper>
        ),
      )}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button type="submit" variant="contained" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button
          type="button"
          variant="outlined"
          disabled={save.isPending}
          onClick={() => reset(toFormValues(settings))}
        >
          Reset
        </Button>
      </Box>
    </Box>
  );
}

/**
 * System Config (PC234). With the release switch `system-config-all` on it lists every setting of the
 * reference (walk of 2026-10-23), in its groups and order, the groups collapsed as on the reference;
 * the ones 212Ez does not act on yet say so. With it off, only the settings 212Ez reads.
 */
export function SystemConfigPage() {
  const settings = useSettings();
  const [logOpen, setLogOpen] = useState(false);

  return (
    <Box>
      <PageHeader
        title="System Config"
        code="PC234"
        actions={
          <Button variant="outlined" startIcon={<HistoryIcon />} onClick={() => setLogOpen(true)}>
            Config Changes logs
          </Button>
        }
      />
      {settings.isPending ? (
        <LoadingState />
      ) : settings.error ? (
        <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />
      ) : (
        <SettingsForm settings={settings.data} />
      )}
      <ChangesDialog open={logOpen} onClose={() => setLogOpen(false)} />
    </Box>
  );
}
